/**
 * PAP Analytics Integration for Learn Dropbox
 * 
 * This utility provides simple HTTP-based analytics logging to Dropbox's
 * PAP (Product Analytics Platform) via the server endpoint.
 * 
 * Features:
 * - Simple fetch-based requests (no external dependencies)
 * - Cookie-based authentication 
 * - Graceful error handling (never breaks user experience)
 * - Support for both exposure and business events
 * - Automatic deduplication to prevent spam
 */

const PAP_ENDPOINT = 'https://www.dropbox.com/2/cms_growthbook/log_pap_event';

// Session-based deduplication tracking
let loggedEvents = new Set();
let loggingInProgress = new Set();

/**
 * Log a PAP analytics event
 * @param {string} experimentKey - GrowthBook experiment/feature flag name
 * @param {string} variant - Which variant was shown ("control", "treatment", etc.)
 * @param {string} eventType - Either "exposure" or "business_event"
 * @returns {Promise<Object|null>} - Response object or null if failed
 */
export async function logPapEvent(experimentKey, variant, eventType) {
  // Only run in browser environment
  if (typeof window === 'undefined') {
    return null;
  }

  // Validate required parameters
  if (!experimentKey || !variant || !eventType) {
    console.warn('[PAP Analytics] Missing required parameters:', { experimentKey, variant, eventType });
    return null;
  }

  // Skip OFF/inactive variants
  if (!variant || variant.toLowerCase() === "off") {
    return null;
  }

  // Create unique key for deduplication
  const eventKey = `${experimentKey}_${variant}_${eventType}`;
  
  // Check if already logged or currently processing
  if (loggedEvents.has(eventKey) || loggingInProgress.has(eventKey)) {
    return null; // Silent skip
  }

  // Mark as in progress
  loggingInProgress.add(eventKey);

  try {
    const response = await fetch(PAP_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include', // Important: includes cookies for authentication
      body: JSON.stringify({
        experiment_key: experimentKey,
        variant: variant,
        event_type: eventType
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    // Mark as successfully logged
    loggedEvents.add(eventKey);
    
    return result;
    
  } catch (error) {
    console.warn('[PAP Analytics] Failed to log event (non-blocking):', {
      experiment: experimentKey,
      variant,
      eventType,
      error: error.message
    });
    return null;
  } finally {
    // Always clean up progress tracking
    loggingInProgress.delete(eventKey);
  }
}

/**
 * Log feature flag exposure (when user sees a variant)
 * @param {string} experimentKey - GrowthBook experiment key
 * @param {string} variant - Variant shown to user
 * @returns {Promise<Object|null>}
 */
export async function logExposure(experimentKey, variant) {
  return logPapEvent(experimentKey, variant, 'exposure');
}

/**
 * Log business event (when user takes action related to experiment)
 * @param {string} experimentKey - GrowthBook experiment key  
 * @param {string} variant - Variant the user is in
 * @returns {Promise<Object|null>}
 */
export async function logBusinessEvent(experimentKey, variant) {
  return logPapEvent(experimentKey, variant, 'business_event');
}

/**
 * React hook for analytics logging
 * @returns {Object} Analytics functions
 */
export function useAnalytics() {
  return {
    logPapEvent,
    logExposure,
    logBusinessEvent
  };
}

/**
 * Clear logged events cache (useful for testing)
 * @returns {void}
 */
export function clearEventCache() {
  loggedEvents.clear();
  loggingInProgress.clear();
}

/**
 * Get analytics status for debugging
 * @returns {Object} Current analytics state
 */
export function getAnalyticsStatus() {
  return {
    endpoint: PAP_ENDPOINT,
    loggedEventsCount: loggedEvents.size,
    inProgressCount: loggingInProgress.size,
    loggedEvents: Array.from(loggedEvents),
    inProgress: Array.from(loggingInProgress)
  };
}

if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window._papAnalytics = {
    logPapEvent,
    logExposure,
    logBusinessEvent,
    clearEventCache,
    getStatus: getAnalyticsStatus
  };
  
}